<template>
  <div>
    <Breadcrumb>
      <BreadcrumbItem>首页</BreadcrumbItem>
      <BreadcrumbItem>活动管理</BreadcrumbItem>
      <BreadcrumbItem>活动日历</BreadcrumbItem>
    </Breadcrumb>
    <div class="single-page ivu-article meeting-calendar-page">
      <div class="meeting-calendar-toolbar">
        <Input v-model.trim="searchKeyword" @on-enter="fetchMonthMeetings" size="large" placeholder="输入活动名称搜索">
          <Button slot="append" type="primary" icon="ios-search" @click="fetchMonthMeetings">搜索</Button>
        </Input>
        <span v-if="loginUser.parentAdminUserId !== 1 && loginUser.orgId" class="all-meetings-switch">
          <i-switch v-model="includeParentOrgMeetings" @on-change="fetchMonthMeetings"></i-switch>
          <span class="all-meetings-switch-label">查看所有活动</span>
        </span>
      </div>

      <Card>
        <p slot="title" class="meeting-calendar-title">
          <Icon type="ios-calendar-outline"></Icon>
          活动日历
        </p>
        <div slot="extra" class="meeting-calendar-extra">
          <router-link :to="{name: 'meeting-list'}">
            <Icon type="ios-list-outline"></Icon>
            列表
          </router-link>
          <router-link :to="{name: 'meeting-create'}">
            <Icon type="ios-paperplane"></Icon>
            创建活动
          </router-link>
        </div>

        <div class="meeting-calendar-layout">
          <div class="meeting-calendar">
            <div class="meeting-calendar-head">
              <Button type="text" icon="ios-arrow-back" @click="changeMonth(-1)"></Button>
              <strong>{{ currentMonthText }}</strong>
              <Button type="text" icon="ios-arrow-forward" @click="changeMonth(1)"></Button>
            </div>
            <div class="meeting-calendar-weekdays">
              <span v-for="weekday in weekdays" :key="weekday">{{ weekday }}</span>
            </div>
            <div class="meeting-calendar-grid">
              <button
                v-for="day in calendarDays"
                :key="day.date"
                type="button"
                class="meeting-calendar-day"
                :class="{
                  'is-muted': !day.inCurrentMonth,
                  'is-today': day.isToday,
                  'is-selected': day.date === selectedDate,
                  'has-events': day.count > 0
                }"
                @click="selectDate(day)">
                <span class="day-number">{{ day.dayOfMonth }}</span>
                <span v-if="day.count > 0" class="day-count">{{ day.count }}</span>
              </button>
            </div>
          </div>

          <div class="meeting-calendar-list">
            <div class="meeting-calendar-list-head">
              <div>
                <strong>{{ selectedDateText }}</strong>
                <span>{{ selectedMeetings.length }} 个活动</span>
              </div>
              <button
                class="day-list-button"
                type="button"
                :disabled="selectedMeetings.length === 0"
                @click="openDayInList">查看当天列表</button>
            </div>

            <Spin v-if="loading || dayMeetingsLoading" fix></Spin>
            <div v-if="!loading && !dayMeetingsLoading && selectedMeetings.length === 0" class="meeting-calendar-empty">
              当天暂无活动
            </div>
            <div v-for="meeting in selectedMeetings" :key="meeting.id" class="meeting-calendar-item">
              <div class="meeting-calendar-item-time">{{ formatDateRange(meeting) }}</div>
              <h3>{{ meeting.title }}</h3>
              <p>{{ meeting.onLineMeeting ? '线上活动' : (meeting.addressDetail || '地点待定') }}</p>
              <div class="meeting-calendar-item-actions">
                <Tag>{{ getStatusText(meeting.status) }}</Tag>
                <Button size="small" @click="$router.push(`/meetings/${meeting.id}/detail`)">活动详情</Button>
                <Button size="small" @click="$router.push(`/meetings/${meeting.id}/signs`)">报名管理</Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  name: 'MeetingCalendar',
  data () {
    return {
      currentMonth: moment().startOf('month'),
      selectedDate: moment().format('YYYY-MM-DD'),
      monthDateCounts: {},
      monthTotalCount: 0,
      searchKeyword: null,
      includeParentOrgMeetings: false,
      loading: false,
      calendarRequestToken: 0,
      selectedDayMeetings: [],
      dayMeetingsLoading: false,
      dayMeetingsRequestToken: 0,
      weekdays: ['一', '二', '三', '四', '五', '六', '日']
    }
  },
  computed: {
    ...mapState({
      loginUser: 'user'
    }),
    currentMonthText () {
      return this.currentMonth.format('YYYY年MM月')
    },
    selectedDateText () {
      return moment(this.selectedDate).format('YYYY年MM月DD日')
    },
    calendarDays () {
      let firstDay = this.currentMonth.clone().startOf('month')
      let startDay = firstDay.clone().subtract(firstDay.isoWeekday() - 1, 'days')
      let days = []
      for (let i = 0; i < 42; i++) {
        let day = startDay.clone().add(i, 'days')
        let date = day.format('YYYY-MM-DD')
        days.push({
          date,
          dayOfMonth: day.date(),
          inCurrentMonth: day.month() === this.currentMonth.month(),
          isToday: date === moment().format('YYYY-MM-DD'),
          count: Number(this.monthDateCounts[date] || 0)
        })
      }
      return days
    },
    selectedMeetings () {
      return this.selectedDayMeetings
    }
  },
  methods: {
    monthStartTimestamp () {
      return this.currentMonth.clone().startOf('month').valueOf()
    },
    monthEndTimestamp () {
      return this.currentMonth.clone().endOf('month').valueOf()
    },
    dayStartTimestamp () {
      return moment(this.selectedDate).startOf('day').valueOf()
    },
    dayEndTimestamp () {
      return moment(this.selectedDate).endOf('day').valueOf()
    },
    getMeetingDates (meeting) {
      if (!meeting.startDate) {
        return []
      }
      let start = moment(meeting.startDate).startOf('day')
      let end = meeting.endDate ? moment(meeting.endDate).startOf('day') : start.clone()
      if (end.isBefore(start)) {
        end = start.clone()
      }
      let monthStart = this.currentMonth.clone().startOf('month')
      let monthEnd = this.currentMonth.clone().endOf('month')
      start = moment.max(start, monthStart)
      end = moment.min(end, monthEnd)

      let dates = []
      let cursor = start.clone()
      while (cursor.isSameOrBefore(end, 'day')) {
        dates.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
      }
      return dates
    },
    buildCalendarQuery () {
      let query = [
        `includeParentOrgMeetings=${this.includeParentOrgMeetings}`,
        'summaryOnly=true',
        `startDate=${this.monthStartTimestamp()}`,
        `endDate=${this.monthEndTimestamp()}`
      ]
      if (this.searchKeyword) {
        query.push(`name=${encodeURIComponent(this.searchKeyword)}`)
      }
      return query.join('&')
    },
    normalizeCalendarDateCounts (resp) {
      let map = {}
      let dates = Array.isArray(resp && resp.dates) ? resp.dates : []
      if (dates.length) {
        dates.forEach(item => {
          let date = String((item && (item.date || item.dateKey)) || '').trim()
          let count = Number((item && item.count) || 0)
          if (date && count > 0) {
            map[date] = count
          }
        })
        return map
      }
      let meetings = Array.isArray(resp && resp.meetings) ? resp.meetings : []
      meetings.forEach(meeting => {
        this.getMeetingDates(meeting).forEach(date => {
          map[date] = Number(map[date] || 0) + 1
        })
      })
      return map
    },
    buildDayQuery (page, size) {
      let query = [
        `page=${page}`,
        `size=${size}`,
        `includeParentOrgMeetings=${this.includeParentOrgMeetings}`,
        `startDate=${this.dayStartTimestamp()}`,
        `endDate=${this.dayEndTimestamp()}`
      ]
      if (this.searchKeyword) {
        query.push(`name=${encodeURIComponent(this.searchKeyword)}`)
      }
      return query.join('&')
    },
    async fetchMonthMeetings () {
      let requestToken = ++this.calendarRequestToken
      try {
        this.loading = true
        this.dayMeetingsRequestToken += 1
        this.dayMeetingsLoading = false
        this.selectedDayMeetings = []
        let resp = (await this.$http.get(`/api/admin/meetings/calendar?${this.buildCalendarQuery()}`)).body
        if (requestToken !== this.calendarRequestToken) {
          return
        }
        this.monthDateCounts = this.normalizeCalendarDateCounts(resp)
        this.monthTotalCount = Number(resp.totalCount || 0)
        this.fetchSelectedDayMeetings()
      } catch (e) {
        if (requestToken === this.calendarRequestToken) {
          this.$Message.error((e.body && e.body.error) || '加载活动失败')
          this.monthDateCounts = {}
          this.monthTotalCount = 0
          this.selectedDayMeetings = []
        }
      } finally {
        if (requestToken === this.calendarRequestToken) {
          this.loading = false
        }
      }
    },
    async fetchSelectedDayMeetings () {
      let requestToken = ++this.dayMeetingsRequestToken
      let page = 1
      let pageSize = 500
      let meetings = []
      try {
        this.dayMeetingsLoading = true
        while (true) {
          let resp = (await this.$http.get(`/api/admin/meetings?${this.buildDayQuery(page, pageSize)}`)).body
          if (requestToken !== this.dayMeetingsRequestToken) {
            return
          }
          let items = resp.meetings || []
          meetings = meetings.concat(items)
          let totalCount = Number(resp.totalCount || meetings.length)
          if (!items.length || meetings.length >= totalCount) {
            break
          }
          page += 1
        }
        this.selectedDayMeetings = meetings
      } catch (e) {
        if (requestToken === this.dayMeetingsRequestToken) {
          this.$Message.error((e.body && e.body.error) || '加载当天活动失败')
          this.selectedDayMeetings = []
        }
      } finally {
        if (requestToken === this.dayMeetingsRequestToken) {
          this.dayMeetingsLoading = false
        }
      }
    },
    changeMonth (step) {
      this.currentMonth = this.currentMonth.clone().add(step, 'months')
      this.selectedDate = this.currentMonth.clone().startOf('month').format('YYYY-MM-DD')
      this.fetchMonthMeetings()
    },
    selectDate (day) {
      if (!day.inCurrentMonth) {
        this.currentMonth = moment(day.date).startOf('month')
        this.selectedDate = day.date
        this.fetchMonthMeetings()
        return
      }
      this.selectedDate = day.date
      this.fetchSelectedDayMeetings()
    },
    openDayInList () {
      this.$router.push({
        name: 'meeting-list',
        query: {
          startDate: this.dayStartTimestamp(),
          endDate: this.dayEndTimestamp(),
          name: this.searchKeyword || undefined,
          includeParentOrgMeetings: this.includeParentOrgMeetings
        }
      })
    },
    getStatusText (status) {
      if (status === 0) {
        return '未发布'
      } else if (status === 1) {
        return '已发布'
      } else if (status === 2) {
        return '报名中'
      } else if (status === 3) {
        return '报名结束'
      } else if (status === 4) {
        return '活动结束'
      }
      return ''
    },
    formatDateRange (meeting) {
      if (!meeting.startDate) {
        return '时间待定'
      }
      let start = moment(meeting.startDate)
      if (!meeting.endDate) {
        return start.format('YYYY-MM-DD HH:mm')
      }
      let end = moment(meeting.endDate)
      if (start.isSame(end, 'day')) {
        return `${start.format('YYYY-MM-DD HH:mm')} - ${end.format('HH:mm')}`
      }
      return `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`
    }
  },
  mounted () {
    this.fetchMonthMeetings()
  }
}
</script>

<style lang="scss">
.meeting-calendar-page {
  .meeting-calendar-toolbar {
    width: 680px;
    max-width: 100%;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .meeting-calendar-title {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .meeting-calendar-extra {
    display: flex;
    gap: 16px;
  }

  .all-meetings-switch {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  .all-meetings-switch-label {
    margin-left: 8px;
  }

  .meeting-calendar-layout {
    display: grid;
    grid-template-columns: minmax(420px, 1fr) 380px;
    gap: 24px;
    align-items: start;
  }

  .meeting-calendar-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 18px;
  }

  .meeting-calendar-weekdays,
  .meeting-calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .meeting-calendar-weekdays span {
    text-align: center;
    color: #808695;
    padding: 8px 0;
  }

  .meeting-calendar-day {
    min-height: 82px;
    border: 1px solid #e8eaec;
    background: #fff;
    text-align: left;
    padding: 8px;
    cursor: pointer;
    position: relative;
  }

  .meeting-calendar-day:hover {
    border-color: #2d8cf0;
    z-index: 1;
  }

  .meeting-calendar-day.is-muted {
    color: #c5c8ce;
    background: #f8f8f9;
  }

  .meeting-calendar-day.is-selected {
    border-color: #2d8cf0;
    box-shadow: inset 0 0 0 1px #2d8cf0;
    z-index: 2;
  }

  .meeting-calendar-day.is-today .day-number {
    color: #2d8cf0;
    font-weight: bold;
  }

  .day-count {
    position: absolute;
    right: 8px;
    bottom: 8px;
    min-width: 22px;
    height: 22px;
    line-height: 22px;
    text-align: center;
    border-radius: 11px;
    color: #fff;
    background: #ed4014;
    font-size: 12px;
  }

  .meeting-calendar-list {
    position: relative;
    min-height: 240px;
    border-left: 1px solid #e8eaec;
    padding-left: 24px;
  }

  .meeting-calendar-list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .meeting-calendar-list-head span {
    display: block;
    margin-top: 4px;
    color: #808695;
  }

  .day-list-button {
    min-height: 24px;
    padding: 0 7px;
    border: 1px solid #2d8cf0;
    border-radius: 3px;
    background: #2d8cf0;
    color: #fff;
    font-size: 12px;
    line-height: 1.5;
    cursor: pointer;
  }

  .day-list-button:disabled {
    color: #c5c8ce;
    background: #f7f7f7;
    border-color: #dcdee2;
    cursor: not-allowed;
  }

  .meeting-calendar-empty {
    padding: 60px 0;
    text-align: center;
    color: #808695;
  }

  .meeting-calendar-item {
    border-bottom: 1px solid #e8eaec;
    padding: 14px 0;
  }

  .meeting-calendar-item-time {
    color: #2d8cf0;
    margin-bottom: 6px;
  }

  .meeting-calendar-item h3 {
    font-size: 16px;
    line-height: 1.5;
    margin: 0 0 6px;
  }

  .meeting-calendar-item p {
    color: #808695;
    margin-bottom: 10px;
  }

  .meeting-calendar-item-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/meeting/Calendar.vue